import { useEffect, useState, type FormEvent } from "react";
import { AccountPicker, NO_SELECTION, type Selection } from "./AccountPicker.js";
import { ApiError, createMap, fetchAccount, importNewMap } from "./api.js";
import { go } from "./nav.js";
import styles from "./NewMap.module.css";

const SALESFORCE_ID = /^[A-Za-z0-9]{15}(?:[A-Za-z0-9]{3})?$/;

/**
 * New value map (brief 8.1): choose the Account and, optionally, the Opportunity, then open the
 * map. Arrives with the Account and Opportunity filled in from the Salesforce "New value map"
 * button (`/new?accountId=...&opportunityId=...`). A reference-tool export can start the map.
 */
export function NewMap() {
  const params = new URLSearchParams(window.location.search);
  const accountParam = params.get("accountId") ?? "";
  const opportunityParam = params.get("opportunityId") ?? "";
  const [selection, setSelection] = useState<Selection>(NO_SELECTION);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!SALESFORCE_ID.test(accountParam)) return;
    const ctl = new AbortController();
    fetchAccount(accountParam, ctl.signal).then(
      (account) => account && setSelection({ account, opportunityId: "" }),
      () => undefined,
    );
    return () => ctl.abort();
  }, [accountParam]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!selection.account || busy) return;
    setBusy(true);
    setError(null);
    const input = {
      accountId: selection.account.id,
      opportunityId: selection.opportunityId || undefined,
      title: title.trim() || undefined,
    };
    try {
      let data: unknown;
      if (file) {
        try {
          data = JSON.parse(await file.text());
        } catch {
          throw new ApiError(
            "That file could not be read. Choose a JSON export from the Value Map Builder.",
            400,
          );
        }
      }
      const { id } = file ? await importNewMap(input, data) : await createMap(input);
      go(`/maps/${encodeURIComponent(id)}`);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "The value map could not be created. Please try again.",
      );
      setBusy(false);
    }
  }

  return (
    <main className={styles.wrap}>
      <div className={styles.eyebrow}>SalesFix staff</div>
      <h1>New value map</h1>
      <p className={styles.intro}>
        A value map belongs to a client Account in Salesforce, and optionally to one of its
        Opportunities.
      </p>
      <form className={styles.card} onSubmit={submit}>
        {error && (
          <div className={styles.alert} role="alert">
            {error}
          </div>
        )}
        <AccountPicker
          selection={selection}
          onChange={setSelection}
          initialOpportunityId={opportunityParam}
        />
        <label className={styles.label} htmlFor="map-title">
          Title (optional)
        </label>
        <input
          id="map-title"
          type="text"
          maxLength={255}
          placeholder="Defaults to the Account name followed by value map"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <label className={styles.label} htmlFor="map-file">
          Start from a file (optional)
        </label>
        <input
          id="map-file"
          type="file"
          accept="application/json,.json"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
        <p className={styles.note}>
          Use a JSON export from the Value Map Builder or the original tool. Goals, value areas,
          calculations and ratings come across unchanged.
        </p>
        <div className={styles.actions}>
          <a className={styles.cancel} href="/">
            Cancel
          </a>
          <button type="submit" className={styles.primary} disabled={!selection.account || busy}>
            {busy ? "Creating…" : "Create value map"}
          </button>
        </div>
      </form>
    </main>
  );
}
